<template>
  <div class="limits">
    <AppUpsellBanner
      v-if="!limitsEnabled && !showDialogLoading"
      :feature="limitsFeature"
      :organization-id="organization.id"
      track-click
      :can-manage-billing="canManageBilling"
    />
    <template v-if="!expectedWorkEnabled">
      <div class="row">
        <div class="col-sm-6 col-lg-3 col-xs-12">
          <ValidationProvider
            v-slot="{ errors }"
            ref="weekly_limit"
            name="weekly_limit"
            :rules="`greaterThanZero|max_value:${MAX_WEEKLY_LIMIT}`"
          >
            <div
              id="recurring-weekly-limit"
              class="form-group mb-5"
              :class="{ 'has-error': errors[0] }"
            >
              <label class="control-label">
                <div class="d-flex align-items-center">
                  Recurring weekly limit
                  <i
                    v-tooltip
                    class="hi hi-info-filled hi-12 a-tooltip mx-5"
                    title="Limits how much time can be tracked each week"
                  ></i>
                </div>
              </label>
              <div class="input-group">
                <AppInput
                  v-model="weeklyLimit"
                  placeholder="No weekly limit"
                  :disabled="!limitsEnabled"
                />
                <span class="input-group-addon"> hrs/wk </span>
              </div>
              <div
                v-if="errors[0]"
                class="mb-20"
              >
                <span class="help-block">
                  {{ errors[0] }}
                </span>
              </div>
              <div
                v-else
                class="small help-block has-justified-children mb-20"
              >
                <span class="value">
                  {{ weeklyLimitStatus }}
                </span>
                <span v-if="weeklyLimit">
                  <a
                    class="remove"
                    @click="removeWeekly"
                  >
                    Remove
                  </a>
                </span>
              </div>
            </div>
          </ValidationProvider>
        </div>
        <div
          v-if="totalWeeklyLimit"
          class="col-sm-6 col-lg-3 col-xs-12"
        >
          <div
            id="this-week-limit"
            class="form-group mb-20"
          >
            <label class="control-label"> This week </label>
            <div class="progress-details">
              <div class="tracked-wrapper">
                <span class="tracked">
                  {{ formatLimitDuration(trackedWeeklyLimit) }}
                </span>
                <span class="progress-text-divider"></span>
                <span
                  class="total"
                  :class="{ 'limit-changed': currentWeeklyLimitChanged }"
                >
                  {{ formatLimitDuration(totalWeeklyLimit) }}
                </span>
              </div>
              <MemberDialogTabLimitsPopover
                limit-type="weekly"
                @limit-change="onCurrentLimitChange"
              >
                <template #trigger>
                  <a class="progress-action">Edit limit</a>
                </template>
              </MemberDialogTabLimitsPopover>
            </div>
            <AppProgressBar
              :tracked="trackedWeeklyLimit"
              :total="totalWeeklyLimit"
            />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6 col-lg-3 col-xs-12">
          <div id="recurring-daily-limit">
            <label class="control-label">
              <div class="d-flex align-items-center">
                Recurring daily limit
                <i
                  v-tooltip
                  class="hi hi-info-filled hi-12 a-tooltip mx-5"
                  title="Limits how much time can be tracked each day"
                ></i>
              </div>
            </label>
            <div
              id="limit-by-shifts"
              class="form-group mb-10 mt-5"
            >
              <ValidationProvider
                :model-value="limitByShifts"
              >
                <div class="checkbox">
                  <AppOnOffToggle
                    v-model="limitByShifts"
                    title="When this setting is enabled, the duration of the scheduled shifts will be applied as the daily limit"
                    :disabled="!limitsEnabled"
                  >
                    <span> Use shifts to set daily limits </span>
                  </AppOnOffToggle>
                </div>
              </ValidationProvider>
            </div>
            <div v-if="!limitByShifts">
              <ValidationProvider
                v-slot="{ errors }"
                ref="daily_limit"
                name="daily_limit"
                :rules="`greaterThanZero|max_value:${MAX_DAILY_LIMIT}`"
              >
                <div
                  id="daily-limit"
                  class="form-group"
                  :class="{ 'has-error': errors[0] }"
                >
                  <div class="input-group">
                    <AppInput
                      v-model="dailyLimit"
                      placeholder="No daily limit"
                      :disabled="!limitsEnabled"
                    />
                    <span class="input-group-addon"> hrs/day </span>
                  </div>
                  <div
                    v-if="errors[0]"
                    class="mb-20"
                  >
                    <span class="help-block">
                      {{ errors[0] }}
                    </span>
                  </div>
                  <small
                    v-else
                    class="help-block has-justified-children mb-20"
                  >
                    <span class="value">{{ dailyLimitStatus }}</span>
                    <span v-if="dailyLimit">
                      <a
                        class="remove"
                        @click="removeDaily"
                      >
                        Remove
                      </a>
                    </span>
                  </small>
                </div>
              </ValidationProvider>
            </div>
            <div
              v-else
              class="text-lighter small"
            >
              The daily limit is controlled by the member's shifts and they will not be able to track time outside of
              shifts
            </div>
          </div>
        </div>
        <div
          v-if="totalDailyLimit || limitByShifts"
          class="col-sm-6 col-lg-3 col-xs-12"
        >
          <div
            id="today-limit"
            class="form-group mb-5"
          >
            <label class="control-label"> Today </label>
            <div v-if="limitByShifts">
              <div
                v-if="totalDailyLimitByShifts"
                id="shifts"
              >
                <div class="progress-details">
                  <div class="tracked-wrapper">
                    <span class="tracked">
                      {{ formatLimitDuration(trackedDailyLimitByShifts) }}
                    </span>
                    <span class="progress-text-divider"></span>
                    <span class="total">
                      {{ formatLimitDuration(totalDailyLimitByShifts) }}
                    </span>
                  </div>
                  <a
                    class="progress-action"
                    :href="shiftsLink"
                  >
                    Manage shifts
                  </a>
                </div>
                <AppProgressBar
                  :tracked="trackedDailyLimitByShifts"
                  :total="totalDailyLimitByShifts"
                />
              </div>
              <div
                v-else
                id="shifts"
              >
                <div class="progress-details">
                  <div class="mr-5">
                    <span class="text-lighter"> No shifts </span>
                  </div>
                  <a
                    class="progress-action"
                    :href="shiftsLink"
                  >
                    Create a shift
                  </a>
                </div>
                <AppProgressBar
                  tracked="0"
                  total="100"
                />
              </div>
            </div>
            <div v-else>
              <div class="progress-details">
                <div class="tracked-wrapper">
                  <span class="tracked">
                    {{ formatLimitDuration(trackedDailyLimit) }}
                  </span>
                  <span class="progress-text-divider"></span>
                  <span
                    class="total"
                    :class="{ 'limit-changed': currentDailyLimitChanged }"
                  >
                    {{ formatLimitDuration(totalDailyLimit) }}
                  </span>
                </div>
                <MemberDialogTabLimitsPopover
                  limit-type="daily"
                  @limit-change="onCurrentLimitChange"
                >
                  <template #trigger>
                    <a class="progress-action">Edit limit</a>
                  </template>
                </MemberDialogTabLimitsPopover>
              </div>
              <AppProgressBar
                :tracked="trackedDailyLimit"
                :total="totalDailyLimit"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!limitByShifts"
        class="row mt-5"
      >
        <div class="col-sm-12">
          <ValidationProvider
            ref="dowLimit"
            v-slot="{ errors, invalid }"
            name="dowLimit"
            rules="required"
            :model-value="dowLimit"
            validate-on-mount
          >
            <div
              id="dow-limit"
              class="form-group"
              :class="{ 'has-error': invalid || errors[0] }"
            >
              <label class="control-label"> Days allowed to work </label>
              <div class="input-group mt-5">
                <!-- Contain native input/change from the day checkboxes: the compat
                     provider's wrapper bridge would otherwise re-render mid-toggle
                     (input fires before change) and revert the checkbox; validation
                     is driven by :model-value instead. -->
                <div
                  class="btn-group-spaced"
                  @input.stop
                  @change.stop
                >
                  <div
                    v-for="{ value, text } in sortedWeekdays"
                    :key="value"
                  >
                    <label
                      :for="value"
                      class="ml-10 p-0"
                    >
                      <input
                        :id="value"
                        v-model="dowLimit"
                        class="dow-limit-input"
                        type="checkbox"
                        :value="text"
                        :disabled="!limitsEnabled"
                      />
                      <a
                        class="btn btn-sm btn-default btn-circle dow-limits-btn ml-0"
                        :class="toggleDowLimitBtnClass(text)"
                      >
                        {{ $filters.capitalize(text) }}
                      </a>
                    </label>
                  </div>
                </div>
              </div>
              <span
                v-if="invalid || errors[0]"
                class="help-block"
              >
                Must select at least one day
              </span>
            </div>
          </ValidationProvider>
        </div>
      </div>
    </template>
    <ExpectedWorkDaysForm
      v-else
      :limit-by-shifts="limitByShifts"
      :sorted-weekdays="sortedWeekdays"
      :dow-expected="dowExpected"
      :weekly-limit="weeklyLimit"
      :daily-limit="dailyLimit"
      :expected-per-week="expectedPerWeek"
      :weekly-limit-status="weeklyLimitStatus"
      :daily-limit-status="dailyLimitStatus"
      :dow-limit="dowLimit"
      :shifts-link="shiftsLink"
      :tracked-daily-limit="trackedDailyLimit"
      :tracked-weekly-limit="trackedWeeklyLimit"
      :total-daily-limit="totalDailyLimit"
      :total-weekly-limit="totalWeeklyLimit"
      :tracked-daily-limit-by-shifts="trackedDailyLimitByShifts"
      :total-daily-limit-by-shifts="totalDailyLimitByShifts"
      @on-dow-expected-change="onDowExpectedChange"
      @set-limits-by-shifts="onSetLimitsByShifts"
      @remove-expected-per-week="onRemoveExpectedPerWeek"
      @set-expected-per-week="setExpectedPerWeek"
      @set-weekly-limit="setWeeklyLimit"
      @set-daily-limit="setDailyLimit"
      @set-dow-expected="setDowExpected"
      @remove-recurring-weekly-limit="removeRecurringWeeklyLimit"
      @remove-recurring-daily-limit="removeRecurringDailyLimit"
      @set-dow-limit="setDowLimit"
    />

    <div
      v-if="membership && organization.flags?.includes('flexible_overtime')"
      class="mt-20"
    >
      <hr />
      <h4 class="mb-4">Overtime policies</h4>
      <p class="text-muted mb-10">
        View active and upcoming overtime policies for this member.
        <a :href="managePoliciesUrl">Manage policies.</a>
      </p>
      <turbo-frame
        :id="`member-overtime-policies-${membership.id}`"
        :src="`/user_organizations/${membership.id}/overtime_policies`"
      />
    </div>
  </div>
</template>

<script>
import { MAX_DAILY_LIMIT, MAX_WEEKLY_LIMIT } from '../../app_constants';
import { mapState, mapGetters, mapActions } from 'vuex';
import { mapFormFields } from '~/utils/form_utils';
import { formatDuration } from '~/utils/time_utils';
import MemberDialogTabLimitsPopover from '~/components/members/MemberDialogTabLimitsPopover.vue';
import ExpectedWorkDaysForm from './expected_work_days/ExpectedWorkDaysForm.vue';
import { OrganizationsResource } from '~/resources';

export default {
  name: 'MemberDialogTabLimits',
  components: {
    MemberDialogTabLimitsPopover,
    ExpectedWorkDaysForm,
  },
  data() {
    return {
      currentDailyLimitChanged: false,
      currentWeeklyLimitChanged: false,
      disableOnSpecificDays: false,
      MAX_DAILY_LIMIT,
      MAX_WEEKLY_LIMIT,
    };
  },
  computed: {
    ...mapState(['membership', 'organization', 'current_user']),
    ...mapGetters([
      'canManageBilling',
      'currentUserIsOrganizationOwner',
      'hasFixedRate',
      'sortedWeekdays',
      'showDialogLoading',
      'limitsEnabled',
      'limitsFeature',
      'expectedWorkEnabled',
    ]),
    ...mapFormFields('membership', {
      dailyLimit: 'limits.daily_limit',
      limitByShifts: 'limits.limits_by_shifts',
      trackedDailyLimit: 'limits.current_daily_limit.used',
      trackedWeeklyLimit: 'limits.current_weekly_limit.used',
      totalDailyLimit: 'limits.current_daily_limit.total',
      totalWeeklyLimit: 'limits.current_weekly_limit.total',
      trackedDailyLimitByShifts: 'limits.current_daily_limit_by_shifts.used',
      totalDailyLimitByShifts: 'limits.current_daily_limit_by_shifts.total',
      weeklyLimit: 'limits.weekly_limit',
      expectedPerWeek: 'limits.expected_per_week',
    }),
    dowLimit: {
      get() {
        return this.$store.state.membership.limits.dow_limit;
      },
      set(days) {
        // Flag dirty here instead of on the day button's @click: a click-time re-render
        // resyncs the checkbox from the old model before its change event fires,
        // swallowing the toggle under Vue 3. The ref only exists in the legacy picker
        // branch; ExpectedWorkDaysForm writes here through its lock icons too.
        this.$refs.dowLimit?.setFlags({ dirty: true });
        this.updateDowLimit(days);
      },
    },
    dowExpected: {
      get() {
        return this.membership.limits.dow_expected;
      },
      set(days) {
        this.updateDowExpected(days);
      },
    },
    dailyLimitStatus() {
      let status;
      const validLimit = this.dailyLimit <= this.MAX_DAILY_LIMIT;
      const hasPayRate = this.payRate > 0 && !this.hasFixedRate;

      if (!this.dailyLimit) {
        status = 'No limit';
      } else {
        if (validLimit) {
          if (this.currentUserIsOrganizationOwner && hasPayRate) {
            status = `${this.formatLimitRate(this.dailyLimit)} per day`;
          } else {
            const formattedHours = this.formatLimitDuration(this.dailyLimit * 3600);

            status = `${formattedHours} hours per day`;
          }
        }
      }

      return status;
    },
    payRate() {
      return this.membership && this.membership.payments && this.membership.payments.pay_rate;
    },
    organizationCurrencySymbol() {
      return this.organization.currency.symbol;
    },
    shiftsLink() {
      return OrganizationsResource.shiftsLink({ id: this.organization.id });
    },
    weeklyLimitStatus() {
      const hasPayRate = this.payRate > 0 && !this.hasFixedRate;

      if (!this.weeklyLimit) {
        return 'No limit';
      }
      if (this.currentUserIsOrganizationOwner && hasPayRate) {
        return `${this.formatLimitRate(this.weeklyLimit)} per week`;
      }

      const formattedHours = this.formatLimitDuration(this.weeklyLimit * 3600);

      return `${formattedHours} hours per week`;
    },
    managePoliciesUrl() {
      return `/organizations/${this.organization.id}/settings/overtime`;
    },
  },
  methods: {
    ...mapActions([
      'removeRecurringDailyLimit',
      'removeRecurringWeeklyLimit',
      'removeExpectedPerWeek',
      'updateDowLimit',
      'updateDowExpected',
    ]),
    formatLimitRate(limit) {
      const rate = this.payRate * limit;

      return rate.formatMoney(this.organization.currency);
    },
    formatLimitDuration(limit) {
      return formatDuration(limit, { showSeconds: 'never' });
    },
    toggleDowLimitBtnClass(day) {
      if (!this.limitsEnabled) {
        return 'disabled';
      }
      if (!this.dowLimit || !day) {
        return;
      }

      return this.dowLimit.includes(day) ? 'active' : '';
    },
    removeDaily() {
      this.$refs.daily_limit.setFlags({ dirty: true });

      this.removeRecurringDailyLimit();
    },
    removeWeekly() {
      this.$refs.weekly_limit.setFlags({ dirty: true });

      this.removeRecurringWeeklyLimit();
    },
    onDowExpectedChange(val) {
      this.updateDowExpected(val);
    },
    onSetLimitsByShifts(val) {
      this.limitByShifts = val;
    },
    onRemoveExpectedPerWeek() {
      this.removeExpectedPerWeek();
    },
    setExpectedPerWeek(val) {
      this.expectedPerWeek = +val;
    },
    setWeeklyLimit(val) {
      this.weeklyLimit = val;
    },
    setDailyLimit(val) {
      this.dailyLimit = val;
    },
    setDowExpected(val) {
      this.dowExpected = val;
    },
    onCurrentLimitChange(type) {
      if (type === 'weekly') {
        this.currentWeeklyLimitChanged = true;
      } else {
        this.currentDailyLimitChanged = true;
      }
    },
    // Remove | add 1 day from/for dowLimit;
    // @param event: add | remove;
    // day: Mon, Tue, ...
    setDowLimit({ day, event }) {
      if (event === 'remove') {
        const dayIndex = this.dowLimit.indexOf(day);
        const _dowLimit = this.dowLimit.slice(0, dayIndex).concat(this.dowLimit.slice(dayIndex + 1));

        this.dowLimit = _dowLimit;
      } else {
        this.dowLimit = [...this.dowLimit, day];
      }
    },
  },
};
</script>
